// ─── Impostazioni → Fasce prenotabili ────────────────────────────────────────
//
// 🗣️ Nasce da una richiesta del committente detta per esteso: *«devi darmi la
// possibilità di definire io come voglio gli slot prenotabili»*. ⇒ Gli orari
// del circolo sono un DATO, non una costante dentro il codice.
//
// ⭐ E la schermata mostra l'ANTEPRIMA della griglia (§ 03.6), perché una
// tabella di orari non dice se il giorno ha un buco: una striscia sì.
//
// ✏️ Una fascia già aperta si CAMBIA e si COPIA su altri giorni, invece di
// doverla cancellare e rifare: cancellare e rifare è lo stesso gesto in due
// tempi, e nel mezzo il calendario resta senza quell'ora.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GIORNI,
  ORARIO_CIRCOLO_PREDEFINITO,
  ORDINE_SETTIMANA,
  bandeDalleCaselle,
  caselleDalleFasce,
  caselleNeiLoroSlot,
  laGrigliaLaSaDisegnare,
  oreDellaGriglia,
  type BandaDisegnata,
  type Campo as CampoCircolo,
  type CasellaFascia,
  fasceEfficaci,
  giorniSuoi,
  giornoInComune,
  type Fascia,
  type OrarioCircolo,
} from '@pmo/shared';
import { Foglio, type DaScrivere } from '../../features/foglio/Foglio.js';
import { SPENTA, eSpenta, passo as misuraDelPasso, segno } from '../../features/fasce/passo.js';
import { chiama } from '../../lib/api.js';
import { perGiorni } from '../../lib/esiti.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { CampoOra } from '../../components/CampoOra.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'fasce';
const CHIAVE_ORARIO = 'orario-circolo';

/** Il valore del menu «Campo» quando la fascia vale per tutti: `courtId` nullo. */
const TUTTI = 'tutti';

/**
 * ⭐⭐ QUESTA FASCIA IL FOGLIO LA DISEGNA?
 *
 * 🚨 **Una definizione sola, usata dai due che devono essere d'accordo**: il
 * foglio, per decidere cosa colorare, e l'elenco qui sotto, per decidere cosa
 * mostrare. Se fossero due righe diverse, prima o poi una fascia sparirebbe da
 * tutt'e due — invisibile nel foglio perché non disegnabile, e assente
 * dall'elenco perché «tanto la disegna il foglio».
 * 📌 *Due schermate che si dividono un insieme devono dividerlo con la stessa
 *    riga, o la differenza fra le due righe è un posto dove le cose spariscono.*
 */
function laDisegnaIlFoglio(f: Fascia, ore: readonly string[]): boolean {
  /*
   * 🔄 **QUI C'ERA ANCHE `f.courtId !== null`, ed è caduto il 18/09/2026.**
   * Diceva il vero finché il foglio disegnava un mondo solo — quello di «tutti
   * i campi» — e una fascia dedicata non aveva una casella dove stare.
   * 🗣️ Poi lui: *«sì, un campo può avere orari di giorni in comune e orari di
   * giorni suoi»* ⇒ adesso il foglio ha **una linguetta per campo**, e una
   * fascia dedicata la sua casella ce l'ha: sotto il nome del suo campo.
   * ⚠️ Lasciandola qui, ogni fascia per campo sarebbe finita **insieme** nel
   * foglio e nell'elenco «che il foglio non disegna» — cioè disegnata e
   * dichiarata non disegnata nella stessa pagina.
   * 📌 *Una condizione che descriveva un limite va riletta quando il limite
   *    cade, o continua a escludere per un motivo che non esiste più.*
   */
  if (f.validFrom !== null || f.validTo !== null) return false;
  if (!laGrigliaLaSaDisegnare(f)) return false;
  // E dev'esserci la RIGA su cui disegnarla: fuori dall'orario del circolo la
  // griglia non ha dove metterla.
  const dentro = new Set(ore);
  return caselleDalleFasce([f]).every((c) => dentro.has(c.time));
}

/**
 * Il nome del campo, o «tutti i campi».
 *
 * ⚠️ Un campo che non si trova non diventa «tutti»: diventa un avviso. Una
 * fascia che punta a un campo cancellato è un fatto da vedere, non da
 * arrotondare al caso più comune — che per giunta è il più largo.
 */
function nomeCampo(courtId: string | null, campi: CampoCircolo[]): string {
  if (courtId === null) return 'tutti i campi';
  return campi.find((c) => c.id === courtId)?.name ?? 'campo sconosciuto';
}

export function Fasce(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  /**
   * 🗣️ «non posso duplicare contemporaneamente più fasce» ⇒ la selezione sta
   * QUI e non dentro il giorno: si spuntano quattro fasce del lunedì e una del
   * martedì, e si copiano tutte insieme.
   * ⚠️ Sono ID e non indici: dopo una rilettura le posizioni ballano, gli ID no.
   */
  const [selezionate, setSelezionate] = useState<string[]>([]);
  /**
   * ⭐ Il modulo della fascia nuova sta CHIUSO: il foglio è il modo normale di
   * aprire un'ora, e questo serve solo ai due casi che il foglio non fa — un
   * campo solo, o un periodo. Tenerlo aperto in fondo alla pagina rimetterebbe
   * sotto al foglio proprio la «vecchia impostazione» che lui ha chiesto di
   * togliere.
   */
  const [apriNuova, setApriNuova] = useState(false);
  /**
   * ⭐⭐ **Quale campo si sta guardando. `null` = «tutti i campi», ed è il
   * punto di partenza apposta.**
   *
   * 🗣️ Sua, 18/09/2026: *«voglio fare per tre campi le stesse fasce orarie e su
   * un campo cambiare l'inizio»* · *«sì, un campo può avere orari di giorni in
   * comune e orari di giorni suoi»*.
   * ⚖️ Il caso normale di un circolo è che i campi siano **uguali**, e il caso
   * normale non deve pagare niente: chi apre questa pagina trova la schermata
   * di sempre, e le linguette le usa solo chi ha un campo diverso dagli altri.
   * 📌 *Una funzione nuova che cambia la schermata di chi non la usa non è una
   *    funzione in più: è una funzione a carico di tutti.*
   */
  const [campoScelto, setCampoScelto] = useState<string | null>(null);
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: Fascia[] }>('GET', '/settings/bookable-slots'),
  });
  // I campi servono a dire NOME invece di «un campo solo», e a riempire il menu.
  const campiQuery = useQuery({
    queryKey: ['campi'],
    queryFn: () => chiama<{ items: CampoCircolo[] }>('GET', '/settings/courts'),
  });
  // ⏰ L'orario del circolo: è il contenitore dentro cui le fasce hanno senso,
  //    e da qui in giù è anche il righello su cui le strisce si disegnano.
  const orarioQuery = useQuery({
    queryKey: [CHIAVE_ORARIO],
    queryFn: () => chiama<OrarioCircolo>('GET', '/settings/club-hours'),
  });

  if (elenco.isLoading || campiQuery.isLoading || orarioQuery.isLoading)
    return <Attesa cosa="le fasce prenotabili" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  if (campiQuery.error) return <Guasto errore={campiQuery.error} />;
  if (orarioQuery.error) return <Guasto errore={orarioQuery.error} />;
  const items = elenco.data?.items ?? [];
  const campi = campiQuery.data?.items ?? [];
  const orario = orarioQuery.data ?? ORARIO_CIRCOLO_PREDEFINITO;
  // Una fascia cancellata da qualcun altro non deve restare «selezionata» in un
  // elenco che non la contiene più: la verità è l'elenco, non la spunta.
  const scelte = items.filter((f) => selezionate.includes(f.id));
  // ⚠️ La stessa riga che usa il foglio, non una sua copia (⇒ `laDisegnaIlFoglio`).
  const ore = oreDellaGriglia(orario.opensAt, orario.closesAt);
  const fuoriDalFoglio = items.filter((f) => !laDisegnaIlFoglio(f, ore));

  return (
    <section>
      {/*
        🗣️ **Sua, 18/09/2026**: *«si può ottimizzare tutto questo spazio in alto
        rendendolo più stretto in altezza, così saliamo la tabella?»*.

        ⚖️ **La cura NON è cancellare le spiegazioni, ed è la parte che si
        sbaglia.** Ognuna di queste frasi è nata da una domanda vera — «dove sono
        gli altri tre campi?», «il numero è la durata?» — o dalla correzione di
        una riga diventata falsa. Toglierle farebbe risalire la tabella oggi e
        tornare le domande domani, senza che nessuno colleghi le due cose.
        ⇒ Restano **tutte**, dentro un «come funziona» che nasce **chiuso**: in
        testa alla pagina resta solo ciò che CAMBIA (quante fasce, quanti campi)
        e ciò su cui si AGISCE (l'orario del circolo).
        📌 *Una spiegazione che si legge una volta sola non deve occupare lo
           schermo per sempre: deve essere trovabile, non presente.*
      */}
      <header className="testata testata-fasce">
        <h2>Fasce orarie</h2>
        <p className="tenue piccolo conteggio">
          {items.filter((f) => f.active).length} fasce attive · {campi.length}{' '}
          {campi.length === 1 ? 'campo' : 'campi'}
          {campi.some((c) => !c.active) && ` (${campi.filter((c) => !c.active).length} spenti)`}
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      <details className="come-funziona">
        <summary>Come funziona questa pagina</summary>
        <p>
          Le fasce sono le ore in cui <strong>i soci</strong> possono prenotare.
          {/*
            🚨 QUI C'ERA SCRITTO «dove non c'è una fascia, il calendario non offre
            niente», ed è diventato FALSO il 16/09/2026: adesso il calendario quelle
            ore le disegna, e la segreteria ci prenota. La riga si corregge, non si
            affianca — una schermata che descrive una regola che non esiste più
            manda a cercare un guasto dove c'è una funzione.
          */}{' '}
          Fuori da quelle ore il circolo resta aperto lo stesso: il calendario le mostra in grigio e la
          segreteria può prenotarle.
        </p>
        <p>
          <strong>L’orario del circolo</strong> qui sotto è un’altra cosa: è quello che il calendario disegna,
          uguale tutti i giorni, e dentro cui la segreteria può prenotare sempre. Mezzanotte si scrive{' '}
          <code>24:00</code>.
        </p>
        <p>
          <strong>Nel foglio</strong> si scrive una cifra in una casella e la si trascina: è{' '}
          <strong>ogni quanto si comincia</strong>, non quanto dura la partita. Ogni{' '}
          <strong>riquadro alto</strong> è uno slot, e dentro c’è scritto da che ora a che ora. Dove è scritto{' '}
          <span className="tenue">avanza</span> la fascia c’è ancora, ma un altro slot intero non ci sta più.
        </p>
        {/*
          🗣️ *«c'è solo un campo, invece ce ne dovrebbero essere quattro»*. Il menu
          dei campi mostra ciò che esiste, e se ne esiste uno solo la domanda
          «dove sono gli altri?» deve trovare risposta QUI — non nel menu, che di
          campi inesistenti non può dire niente.
          📌 Una schermata che offre una scelta corta deve dire dove si allunga.
        */}
        <p>
          <strong>I campi</strong> non si creano qui: si creano e si riaccendono in <strong>Campi</strong>.
        </p>
      </details>

      <Orario orario={orario} modificabile={modificabile} />

      <ScegliCampo campi={campi} fasce={items} scelto={campoScelto} scegli={setCampoScelto} />

      <FoglioDelleFasce
        fasce={items}
        campi={campi}
        courtId={campoScelto}
        orario={orario}
        modificabile={modificabile}
      />

      {/*
        🗣️ Sua, 18/09/2026: *«sotto la tabella c'è ancora la vecchia
        impostazione… forse non serve più, si può levare»*.
        ⇒ **Levata, ma non buttata**: l'elenco resta solo per le fasce che il
        foglio NON sa disegnare — dedicate a un campo, con un periodo di
        validità, con gli estremi fuori dalla mezz'ora, o fuori dall'orario del
        circolo. Quando non ce n'è nessuna — che è il caso del circolo oggi —
        qui sotto non compare più niente, ed è la pagina che voleva lui.
        ⛔ Toglierlo del tutto avrebbe reso quelle fasce **invisibili e non
        modificabili da nessuna parte**: continuerebbero a governare il
        calendario senza che nessuna schermata le nomini.
        📌 *Una parte di schermata che sparisce da sé quando non serve è meglio
           di una parte tolta: la seconda toglie anche i casi che non avevi in
           mente quel giorno.*
      */}
      {fuoriDalFoglio.length > 0 && (
        <>
          <h3 className="titolo-sezione">
            {fuoriDalFoglio.length === 1 ? 'Una fascia che' : `${fuoriDalFoglio.length} fasce che`} il foglio
            non disegna
          </h3>
          <p className="tenue piccolo">
            Sono dedicate a un campo solo, hanno un periodo di validità, o non stanno sulla mezz'ora — sul
            foglio non avrebbero una casella dove stare. Si modificano qui.
          </p>
          {ORDINE_SETTIMANA.filter((g) => fuoriDalFoglio.some((f) => f.weekday === g)).map((giorno) => (
            <Giorno
              key={giorno}
              nome={GIORNI[giorno]}
              fasce={fuoriDalFoglio.filter((f) => f.weekday === giorno)}
              campi={campi}
              orario={orario}
              modificabile={modificabile}
              selezionate={selezionate}
              scegli={(id, dentro) =>
                setSelezionate((p) => (dentro ? [...p, id] : p.filter((x) => x !== id)))
              }
            />
          ))}
        </>
      )}

      {modificabile && (
        <div className="tab-listini">
          {apriNuova ? null : (
            <button type="button" className="aggiungi-tabella" onClick={() => setApriNuova(true)}>
              + Fascia per un campo solo, o per un periodo
            </button>
          )}
        </div>
      )}
      {modificabile && apriNuova && <Nuova campi={campi} />}

      {modificabile && scelte.length > 0 && (
        <DuplicaTante fasce={scelte} campi={campi} svuota={() => setSelezionate([])} />
      )}
    </section>
  );
}

/**
 * ⭐⭐ LE LINGUETTE DEI CAMPI — e la cosa che un selettore nudo perderebbe.
 *
 * 🗣️ Sua, 18/09/2026: *«poter differenziare le fasce orarie per campi»*, e poi
 * *«sì, un campo può avere orari di giorni in comune e orari di giorni suoi»*.
 *
 * ⚖️ **Perché un selettore e non le eccezioni disegnate sul foglio unico.**
 * L'eccezione **non è una casella: è un giorno intero** — una fascia dedicata
 * sostituisce tutto il giorno di quel campo (⇒ `fasce-per-campo.ts`, dove la
 * misura sta scritta). Disegnarla dentro le caselle insegnerebbe che al Campo 4
 * «cambia solo alle 09:00», mentre la verità è che **il lunedì del Campo 4 è un
 * altro lunedì**. 📌 *Una schermata che fa credere il falso è peggio di una
 * schermata scomoda: la seconda si impara, la prima si sbaglia.*
 *
 * ⭐ **E il conto dei giorni suoi sta sulla linguetta**, non dentro. È l'unica
 * cosa che un selettore semplice perde: aprire quattro fogli e ricordarseli è
 * esattamente ciò in cui una persona sbaglia. Così la pagina dice **da chiusa**
 * chi fa storia a sé.
 */
function ScegliCampo({
  campi,
  fasce,
  scelto,
  scegli,
}: {
  campi: CampoCircolo[];
  fasce: Fascia[];
  scelto: string | null;
  scegli(id: string | null): void;
}): React.JSX.Element | null {
  // ⛔ Con un campo solo non c'è niente da differenziare: la fila di linguette
  //    sarebbe una scelta con una voce, cioè rumore. Sparisce da sé.
  if (campi.length < 2) return null;

  return (
    <div className="linguette-campi" role="tablist" aria-label="Di quale campo sono le fasce">
      <button
        type="button"
        role="tab"
        aria-selected={scelto === null}
        className={scelto === null ? 'scelta' : undefined}
        onClick={() => scegli(null)}
      >
        Tutti i campi
      </button>
      {campi.map((c) => {
        const suoi = giorniSuoi(fasce, c.id);
        return (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={scelto === c.id}
            className={[scelto === c.id ? 'scelta' : '', suoi.length > 0 ? 'fa-da-se' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => scegli(c.id)}
          >
            {c.name}
            {suoi.length > 0 && (
              <span className="quanti" title={suoi.map((g) => GIORNI[g]).join(', ')}>
                {suoi.length} {suoi.length === 1 ? 'giorno suo' : 'giorni suoi'}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * ⭐⭐ IL FOGLIO DELLE FASCE — la griglia che si scrive e si trascina.
 *
 * 🗣️ Sua, 18/09/2026: *«è possibile replicare la stessa modalità di inserimento
 * che abbiamo fatto per i listini qui nelle fasce?»* — e sulle spente:
 * *«mi dà la possibilità di accendere e spegnere le fasce orarie spente con un
 * solo clic, anziché doverle ogni volta ripianificare»*.
 *
 * ⛔ **L'ELENCO QUI SOTTO NON SPARISCE**, e non è indecisione: il foglio disegna
 * le fasce **di tutti i campi e senza periodo di validità**, che è come sono
 * tutte quelle del circolo oggi. Una fascia dedicata a un campo, una con le
 * date, o una con gli estremi fuori dalla mezz'ora non hanno una casella dove
 * stare — e si dice quante sono invece di farle sparire.
 * 📌 *Una schermata che mostra una parte deve dire di quale parte sta parlando,
 *    o chi guarda crede di vedere tutto.*
 */
function FoglioDelleFasce({
  fasce,
  campi,
  courtId,
  orario,
  modificabile,
}: {
  fasce: Fascia[];
  campi: CampoCircolo[];
  /** Di quale campo è questo foglio. `null` = quello di tutti. */
  courtId: string | null;
  orario: OrarioCircolo;
  modificabile: boolean;
}): React.JSX.Element {
  const qc = useQueryClient();
  /**
   * ⭐ La pila delle mosse, come nel listino: un trascinamento tocca trenta
   * caselle in un colpo, e senza un passo indietro l'unico rimedio sarebbe
   * riscriverle a memoria.
   */
  const [mosse, setMosse] = useState<DaScrivere[][]>([]);
  const ore = oreDellaGriglia(orario.opensAt, orario.closesAt);

  /**
   * ⭐⭐ Quello che **questo campo applica davvero**: le fasce sue dove ce le
   * ha, quelle di tutti dove non ce le ha. Su «tutti i campi» sono solo le
   * comuni, cioè esattamente il foglio di prima.
   */
  const nostre = fasceEfficaci(fasce, courtId).filter((f) => laDisegnaIlFoglio(f, ore));
  /** I giorni che questo campo eredita da «tutti i campi». */
  // ⚠️ `Set<number>` e non il tipo stretto di `ORDINE_SETTIMANA`: ci si chiede
  //    dentro con il `weekday` di una casella e con quello di una colonna, che
  //    sono numeri qualunque. Un insieme più stretto della domanda non risponde.
  const ereditati = new Set<number>(
    courtId === null ? [] : ORDINE_SETTIMANA.filter((g) => giornoInComune(fasce, courtId, g)),
  );
  const nomeDelCampo = campi.find((c) => c.id === courtId)?.name ?? 'questo campo';

  const caselle = caselleDalleFasce(nostre);
  const dentroLOrario = new Set(ore);
  const visibili = caselle.filter((c) => dentroLOrario.has(c.time));

  /*
   * ⭐⭐ Dove sta ogni casella dentro il SUO slot — apre, continua, chiude, o
   * avanza senza farne parte. È la cosa che l'elenco non sa dire: «si comincia
   * ogni 90 minuti» su una banda di tre ore sono DUE slot, e quali siano
   * bisognava calcolarlo a mente.
   *
   * ⚠️ Nasce dalle BANDE, non dalle caselle: è l'inizio della banda a decidere
   * dove cadono gli slot, e la banda è la corsa di colore.
   */
  const posti = new Map(
    caselleNeiLoroSlot(bandeDalleCaselle(caselle)).map((c) => [`${c.weekday}|${c.time}`, c]),
  );

  const perIlFoglio = visibili.map((c) => {
    const p = posti.get(`${c.weekday}|${c.time}`);
    return {
      weekday: c.weekday,
      time: c.time,
      valore: c.slotMinutes,
      stato: segno({
        spenta: !c.active,
        posto: p?.posto,
        etichetta: p?.da === null || p?.da === undefined ? null : `${p.da}–${p.a}`,
        inComune: ereditati.has(c.weekday),
      }),
    };
  });

  const scrivi = useMutation({
    meta: { fatto: 'fasce prenotabili salvate' },
    mutationFn: (giorni: { weekday: number; bande: Omit<BandaDisegnata, 'weekday'>[] }[]) =>
      chiama('PUT', '/settings/bookable-slots/grid', {
        courtId,
        validFrom: null,
        validTo: null,
        giorni,
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: [CHIAVE] }),
  });

  /**
   * Da un pugno di caselle cambiate al **giorno intero**, che è la sola cosa
   * che il server sa ricevere: una casella non è una riga, quindi «scrivi
   * questa casella» non vuol dire niente.
   */
  function scriviCelle(mod: DaScrivere[]): void {
    const mappa = new Map(caselle.map((c) => [`${c.weekday}|${c.time}`, c]));
    const prima: DaScrivere[] = [];
    for (const m of mod) {
      const k = `${m.weekday}|${m.time}`;
      const c = mappa.get(k);
      // 📸 La fotografia di PRIMA: una casella che non c'era torna a non esserci.
      prima.push(
        c === undefined
          ? { weekday: m.weekday, time: m.time, valore: null }
          : { weekday: m.weekday, time: m.time, valore: c.slotMinutes, stato: c.active ? undefined : SPENTA },
      );
      if (m.valore === null) mappa.delete(k);
      else
        mappa.set(k, {
          weekday: m.weekday,
          time: m.time,
          slotMinutes: m.valore,
          // ⚠️ Chi scrive un numero ACCENDE: si sta dicendo «qui si prenota».
          //    Solo «Spegni» manda uno stato spento, e lo manda apposta.
          active: !eSpenta(m.stato),
        });
    }
    setMosse((p) => [...p.slice(-19), prima]);
    manda([...mappa.values()], mod);
  }

  function manda(tutte: CasellaFascia[], toccate: readonly DaScrivere[]): void {
    const giorniToccati = [...new Set(toccate.map((m) => m.weekday))];
    const bande = bandeDalleCaselle(tutte);
    scrivi.mutate(
      giorniToccati.map((weekday) => ({
        weekday,
        bande: bande.filter((b) => b.weekday === weekday).map(({ weekday: _via, ...resto }) => resto),
      })),
    );
  }

  function annulla(): void {
    const ultima = mosse.at(-1);
    if (ultima === undefined) return;
    setMosse((p) => p.slice(0, -1));
    const mappa = new Map(caselle.map((c) => [`${c.weekday}|${c.time}`, c]));
    for (const m of ultima) {
      const k = `${m.weekday}|${m.time}`;
      if (m.valore === null) mappa.delete(k);
      else
        mappa.set(k, {
          weekday: m.weekday,
          time: m.time,
          slotMinutes: m.valore,
          active: !eSpenta(m.stato),
        });
    }
    manda([...mappa.values()], ultima);
  }

  /**
   * ⭐⭐ **RIMETTI UN GIORNO IN COMUNE**: si cancellano le fasce **proprie** di
   * quel giorno, e il campo torna a seguire tutti gli altri.
   *
   * ⚖️ Si manda un giorno **vuoto** con l'ambito del campo: `riscriviIlGiorno`
   * cancella ciò che trova in quell'ambito e non ci mette niente. ⇒ Nessuna
   * riga dedicata resta, quindi `fasceDelGiorno` ricade sulle comuni.
   * ⛔ **Non tocca le fasce di tutti i campi**, ed è l'errore che sarebbe grave:
   * l'ambito è `{ courtId: <questo campo> }`, quindi le comuni non sono
   * nemmeno nell'insieme che la rotta guarda.
   * ⚠️ E non tocca nemmeno le fasce di questo campo **con un periodo di
   * validità**: l'ambito comprende `validFrom`/`validTo` nulli. Quelle restano
   * nell'elenco qui sotto, dove si sono sempre modificate.
   */
  function rimettiInComune(weekday: number): void {
    // 📸 Niente pila delle mosse: questo non è un tratto di penna, è un cambio
    //    di chi comanda quel giorno. Mescolarlo ai passi indietro del foglio
    //    farebbe tornare indietro due cose diverse con lo stesso bottone.
    setMosse([]);
    scrivi.mutate([{ weekday, bande: [] }]);
  }

  /** Spegnere e riaccendere: il valore resta, cambia solo se vale. */
  function cambiaStato(scelte: { weekday: number; time: string }[], spenta: boolean): void {
    const mappa = new Map(caselle.map((c) => [`${c.weekday}|${c.time}`, c]));
    const mod: DaScrivere[] = [];
    for (const s of scelte) {
      const c = mappa.get(`${s.weekday}|${s.time}`);
      // ⛔ Una casella vuota non si spegne: non c'è niente da far dormire.
      if (c === undefined || c.active !== spenta) continue;
      mod.push({
        weekday: s.weekday,
        time: s.time,
        valore: c.slotMinutes,
        stato: spenta ? SPENTA : undefined,
      });
    }
    if (mod.length > 0) scriviCelle(mod);
  }

  return (
    <div className="pagina-listini">
      {/*
        ⭐ La riga compare SOLO dentro un campo: su «tutti i campi» la pagina
        resta quella di sempre, e chi non ha campi diversi non legge niente in
        più. ⇒ Dice le due cose che servono prima di toccare una casella: cosa
        è grigio, e cosa succede scrivendoci dentro.
        🚨 La seconda è la più importante di tutta la schermata: senza, dare una
        fascia a un campo gli TOGLIE il resto della giornata (⇒ la misura in
        `fasce-per-campo.ts`). Qui non capita — si scrive il giorno intero,
        quindi la copia c'è — ma chi guarda deve sapere che il giorno si sta
        staccando, o si ritrova una struttura diversa senza averla chiesta.
      */}
      {courtId !== null && (
        <p className="tenue piccolo riga-campo">
          In grigio i giorni che <strong>{nomeDelCampo}</strong> prende da «tutti i campi». Scrivendoci
          dentro, quel giorno diventa suo: le fasce di adesso vengono copiate qui, e per i soci non cambia
          niente finché non si modifica qualcosa.
        </p>
      )}
      <Foglio
        ore={ore}
        celle={perIlFoglio}
        misura={misuraDelPasso}
        modificabile={modificabile}
        scrivi={scriviCelle}
        annulla={annulla}
        quanteMosse={mosse.length}
        sottoTestata={
          courtId === null
            ? undefined
            : (g) =>
                ereditati.has(g) ? (
                  <span className="di-chi comune">in comune</span>
                ) : (
                  <span className="di-chi">
                    suo
                    {modificabile && (
                      <button
                        type="button"
                        className="piatto"
                        title={`Cancella le fasce proprie di ${nomeDelCampo} di questo giorno: tornerà a seguire «tutti i campi»`}
                        onClick={() => rimettiInComune(g)}
                      >
                        rimetti in comune
                      </button>
                    )}
                  </span>
                )
        }
        azioni={(scelte) => (
          <>
            <button type="button" className="piatto" onClick={() => cambiaStato(scelte, true)}>
              Spegni
            </button>
            <button type="button" className="piatto" onClick={() => cambiaStato(scelte, false)}>
              Riaccendi
            </button>
          </>
        )}
      />
      <Guasto errore={scrivi.error} />
    </div>
  );
}

/** Da `08:00` ai minuti dall'inizio del giorno: serve solo a disegnare. */
function minuti(ora: string): number {
  const [h, m] = ora.split(':');
  return Number(h) * 60 + Number(m);
}

/**
 * ⭐⭐ L'ORARIO DEL CIRCOLO: il contenitore dentro cui le fasce hanno senso.
 *
 * 🗣️ Richiesta sua del 16/09/2026: *«nel calendario devo vedere le fasce non
 * prenotabili, perché queste non sono prenotabili per i soci, ma noi di
 * segreteria possiamo prenotarle»*. ⇒ Per disegnare le ore «non prenotabili»
 * serviva sapere **fin dove arriva la giornata**, e quel dato non esisteva.
 *
 * ⚖️ Sta in questa schermata e non in una sua perché è la stessa domanda vista
 * da un altro lato: qui si dice quando aprono i soci, qui sopra quando apre il
 * circolo. Separarle in due pagine vorrebbe dire far navigare per capire perché
 * il calendario comincia a quell'ora.
 *
 * 🚨 E queste due ore stavano SCRITTE NEL CODICE, in questo stesso file: la
 * striscia dell'anteprima si disegnava su un righello 08:00–23:00 fisso, che
 * nessuno poteva cambiare e che non corrispondeva a niente. Adesso è lo stesso
 * righello del calendario — cioè quello vero.
 */
function Orario({
  orario,
  modificabile,
}: {
  orario: OrarioCircolo;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE_ORARIO);
  const [apre, setApre] = useState(orario.opensAt);
  const [chiude, setChiude] = useState(orario.closesAt);

  const salva = useMutation({
    meta: { fatto: 'orario del circolo salvato' },
    mutationFn: (m: OrarioCircolo) => chiama<OrarioCircolo>('PATCH', '/settings/club-hours', m),
    onSuccess: rileggi,
  });

  const cambiato = apre !== orario.opensAt || chiude !== orario.closesAt;

  return (
    <div className="orario-circolo">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          salva.mutate({ opensAt: apre, closesAt: chiude });
        }}
      >
        <span className="frase">Il circolo apre alle</span>
        {/* ⚠️ Senza orologio: qui accanto c'è la casella della chiusura, che
            l'orologio non può avere (le 24:00). Due caselle nella stessa frase
            si somigliano o sembrano due cose diverse. */}
        <CampoOra
          value={apre}
          orologio={false}
          aria-label="Ora di apertura"
          className="ora-testo"
          disabled={!modificabile}
          onChange={setApre}
        />
        <span className="frase">e chiude alle</span>
        {/*
          🚨 `24:00` NON è `00:00`, e un `<input type="time">` non lo sa dire: il
          suo massimo è `23:59`. ⇒ Qui è un campo di testo con la sua forma
          dichiarata, e a giudicarlo sono lo schema e il `check` del database.
          ⚖️ Un selettore d'orario che non può esprimere la mezzanotte è peggio di
          una casella: costringerebbe a scrivere `23:59`, e un circolo che chiude
          un minuto prima di mezzanotte è una bugia che poi qualcuno legge.
        */}
        <input
          type="text"
          className="ora-testo"
          value={chiude}
          aria-label="Ora di chiusura"
          inputMode="numeric"
          pattern="([01][0-9]|2[0-3]):[0-5][0-9]|24:00"
          placeholder="24:00"
          maxLength={5}
          disabled={!modificabile}
          onChange={(e) => setChiude(e.target.value)}
        />
        {modificabile && (
          <button type="submit" disabled={!cambiato || salva.isPending}>
            {salva.isPending ? 'Sto salvando…' : 'Salva'}
          </button>
        )}
      </form>

      <Guasto errore={salva.error} />
    </div>
  );
}

function Giorno({
  nome,
  fasce,
  campi,
  orario,
  modificabile,
  selezionate,
  scegli,
}: {
  nome: string;
  fasce: Fascia[];
  campi: CampoCircolo[];
  orario: OrarioCircolo;
  modificabile: boolean;
  selezionate: string[];
  scegli: (id: string, dentro: boolean) => void;
}): React.JSX.Element {
  const attive = fasce.filter((f) => f.active);
  const spente = fasce.filter((f) => !f.active);

  /**
   * ⭐ UNA striscia sola finché le fasce valgono per tutti i campi; UNA PER
   * CAMPO appena qualcuna ne nomina uno.
   *
   * ⚠️ Non è una scelta estetica: mescolando in una riga sola le fasce del
   * Campo 1 e quelle del Campo 3, la striscia direbbe che alle 09:00 si gioca
   * — senza dire DOVE. Sarebbe una barra che risponde a una domanda che nessuno
   * ha fatto, e che tace su quella vera.
   * 📌 E quattro strisce identiche, quando tutte le fasce valgono per tutti,
   *    sarebbero rumore: la forma segue il dato, non il contrario.
   */
  const perCampo = fasce.some((f) => f.courtId !== null);
  const idCitati = new Set(fasce.map((f) => f.courtId).filter((c): c is string => c !== null));
  // I campi in servizio, più quelli che una fascia nomina anche se sono spenti:
  // una fascia appesa a un campo spento va vista, non nascosta.
  const campiDaMostrare = campi.filter((c) => c.active || idCitati.has(c.id));

  return (
    <div className="giorno">
      <h3>
        {nome}{' '}
        {/* ⚠️ Le spente si CONTANO a parte, e si dicono. «chiuso» da solo non
            distingue un giorno senza fasce da un giorno in cui le fasce ci sono
            tutte ma sono spente — e sono due cose che si curano diversamente. */}
        <span className="tenue piccolo">
          {attive.length === 0 ? 'chiuso' : `${attive.length} ${attive.length === 1 ? 'fascia' : 'fasce'}`}
          {spente.length > 0 && ` · ${spente.length} ${spente.length === 1 ? 'spenta' : 'spente'}`}
        </span>
      </h3>

      {perCampo ? (
        campiDaMostrare.map((c) => {
          const suoi = fasce.filter((f) => f.courtId === null || f.courtId === c.id);
          const attiviSuoi = suoi.filter((f) => f.active);
          /**
           * ⭐⭐ SOSTITUZIONE, non unione — è la regola ① di `fasceDelGiorno`, e
           * qui c'era l'unione: la barra prometteva 08:00–23:00 su un campo che
           * il calendario apre solo dalle 12:30.
           * 📌 Un'anteprima che non usa la regola vera non è un'anteprima: è una
           *    seconda griglia, e diverge nel caso preciso per cui esiste.
           * ⚠️ Solo le ATTIVE sostituiscono, come là: una dedicata SPENTA non
           *    toglie di mezzo l'orario generale, perché non prenota niente.
           */
          const dedicate = attiviSuoi.filter((f) => f.courtId !== null);
          return (
            <div key={c.id} className="striscia-campo">
              {/* ⚠️ Un campo spento non prenota niente: il calendario lo esclude
                  del tutto (`campiAttivi`). La barra lo mostra perché una fascia
                  lo nomina, ma lo dice — o prometterebbe ore che non esistono. */}
              <span className="tenue piccolo nome-campo">
                {c.name}
                {c.active ? '' : ' (spento)'}
              </span>
              <Striscia
                attive={dedicate.length > 0 ? dedicate : attiviSuoi}
                spente={suoi.filter((f) => !f.active)}
                orario={orario}
              />
            </div>
          );
        })
      ) : (
        <Striscia attive={attive} spente={spente} orario={orario} />
      )}

      {fasce.length > 0 && (
        <ul className="fasce">
          {fasce.map((f) => (
            <Riga
              key={f.id}
              fascia={f}
              campi={campi}
              modificabile={modificabile}
              scelta={selezionate.includes(f.id)}
              scegli={scegli}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * L'anteprima: la giornata da sinistra a destra, con i buchi visibili.
 *
 * ⭐ Si disegnano TUTTE le fasce, spente comprese — e qui c'era `attive.map`,
 * che le faceva sparire. Una fascia spenta che non compare lascia la striscia
 * IDENTICA a quella di un'ora in cui nessuno ha mai aperto niente: il gesto di
 * spegnere non si vedeva da nessuna parte, e per riaccenderla bisognava
 * ricordarsi che esisteva.
 * 📌 La striscia deve distinguere TRE stati, non due: aperto · spento · mai
 *    esistito. Con due colori se ne raccontano solo due.
 *
 * ⚠️ LIMITE DICHIARATO: la regola ② di `fasceDelGiorno` — fra più periodi di
 * validità vince il più stretto — qui NON si applica, e non per dimenticanza:
 * dipende da una DATA, e questa schermata mostra la settimana tipo, che una
 * data non ce l'ha. ⇒ Con dei periodi dichiarati la barra li disegna tutti
 * sovrapposti; il periodo di ciascuna fascia sta scritto sulla sua riga.
 */
function Striscia({
  attive,
  spente,
  orario,
}: {
  attive: Fascia[];
  spente: Fascia[];
  orario: OrarioCircolo;
}): React.JSX.Element {
  /*
   * 🚨 QUI C'ERANO DUE COSTANTI, `APERTURA = 8*60` e `CHIUSURA = 23*60`, e
   * nessuno poteva cambiarle. ⇒ La striscia disegnava le fasce su un righello
   * inventato: un circolo aperto fino a mezzanotte vedeva le sue ultime ore
   * schiacciate contro il bordo destro, e un'anteprima che mente è peggio di
   * un'anteprima che manca.
   * ⭐ Adesso il righello è lo stesso del calendario, cioè quello vero.
   */
  const apertura = minuti(orario.opensAt);
  const giornata = Math.max(1, minuti(orario.closesAt) - apertura);
  return (
    <div className="striscia" aria-hidden="true">
      {/* ⚠️ Le spente stanno SOTTO (prima nel DOM): se una spenta e una accesa si
          accavallano — cosa che il database permette, perché il vincolo di non
          sovrapposizione vale `where (active)` — a vincere dev'essere ciò che è
          prenotabile davvero. */}
      {[...spente, ...attive].map((f) => {
        const da = Math.max(minuti(f.startsAtTime), apertura);
        const a = Math.min(minuti(f.endsAtTime), apertura + giornata);
        return (
          <span
            key={f.id}
            className={f.active ? 'blocco' : 'blocco spento'}
            style={{
              left: `${((da - apertura) / giornata) * 100}%`,
              width: `${((a - da) / giornata) * 100}%`,
            }}
            title={`${f.startsAtTime}–${f.endsAtTime}${f.active ? '' : ' (spenta)'}`}
          />
        );
      })}
    </div>
  );
}

function Riga({
  fascia,
  campi,
  modificabile,
  scelta,
  scegli,
}: {
  fascia: Fascia;
  campi: CampoCircolo[];
  modificabile: boolean;
  scelta: boolean;
  scegli: (id: string, dentro: boolean) => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  /**
   * Un pannello per volta: due moduli aperti sulla stessa riga si darebbero
   * fastidio. Il bottone è un interruttore e tiene sempre il suo nome — a dire
   * che è aperto ci pensa `aria-expanded`, e il pannello che compare sotto.
   */
  const [aperto, setAperto] = useState<'niente' | 'modifica'>('niente');
  const cambia = useMutation({
    meta: {
      fatto: (_d, c) => ((c as { active: boolean }).active ? 'fascia riattivata' : 'fascia disattivata'),
    },
    mutationFn: (c: { active: boolean }) => chiama('PATCH', `/settings/bookable-slots/${fascia.id}`, c),
    onSuccess: rileggi,
  });
  const elimina = useMutation({
    meta: { fatto: 'fascia eliminata' },
    mutationFn: () => chiama('DELETE', `/settings/bookable-slots/${fascia.id}`),
    onSuccess: rileggi,
  });

  return (
    <li className={fascia.active ? '' : 'spento'}>
      {/*
        ⭐ La spunta è l'UNICA strada per duplicare, e ha sostituito il bottone
        «Duplica» che stava su ogni riga.
        ⚠️ Due meccanismi per lo stesso gesto — uno per una fascia, uno per tante
           — sarebbero due cose da imparare invece di una, e si comporterebbero
           diversamente proprio nel caso in cui uno si aspetta che siano uguali.
           Una fascia sola è semplicemente una spunta sola.
      */}
      {modificabile && (
        <input
          type="checkbox"
          className="scelta-fascia"
          checked={scelta}
          onChange={(e) => scegli(fascia.id, e.target.checked)}
          aria-label={`Scegli la fascia ${fascia.startsAtTime}–${fascia.endsAtTime}`}
        />
      )}
      <strong>
        {fascia.startsAtTime}–{fascia.endsAtTime}
      </strong>
      {/*
        ⭐ Lo stato si dice con una PAROLA, non solo con lo sbiadito.
        ⚠️ L'opacità da sola (`li.spento`) non basta e non è un dettaglio di
           gusto: chi non distingue bene i colori, chi guarda il telefono al
           sole o chi legge con un lettore di schermo non la riceve affatto —
           e sono esattamente i tre casi in cui una fascia spenta viene scambiata
           per una aperta.
      */}
      {!fascia.active && <span className="etichetta-spenta">spenta</span>}
      <span className="tenue piccolo">
        {/* ⚠️ Qui c'era «N min», che si leggeva come la DURATA della partita. Non
            lo è mai stato: è ogni quanto si può COMINCIARE. La durata la sceglie
            chi prenota, e questo numero non la limita. */}
        si comincia ogni {fascia.slotMinutes} min
        {fascia.validFrom || fascia.validTo
          ? ` · dal ${fascia.validFrom ?? '…'} al ${fascia.validTo ?? '…'}`
          : ''}
        {' · '}
        {nomeCampo(fascia.courtId, campi)}
      </span>
      {modificabile && (
        <>
          <button
            type="button"
            className="piatto"
            aria-expanded={aperto === 'modifica'}
            onClick={() => setAperto((a) => (a === 'modifica' ? 'niente' : 'modifica'))}
          >
            Modifica
          </button>
          <button
            type="button"
            className="piatto"
            disabled={cambia.isPending}
            onClick={() => cambia.mutate({ active: !fascia.active })}
          >
            {fascia.active ? 'Spegni' : 'Riaccendi'}
          </button>
          <button
            type="button"
            className="piatto pericolo"
            disabled={elimina.isPending}
            onClick={() => elimina.mutate()}
          >
            Elimina
          </button>
        </>
      )}
      <Guasto errore={cambia.error ?? elimina.error} />

      {modificabile && aperto === 'modifica' && (
        <Modifica fascia={fascia} campi={campi} chiudi={() => setAperto('niente')} />
      )}
    </li>
  );
}

/**
 * Ogni quanto si può COMINCIARE, in minuti.
 *
 * 🗣️ Dalle sue parole del 16/09: *«è da 90 minuti, ma si può iniziare… in
 * qualsiasi tipo orario, ogni 30 minuti»*. ⇒ Il 90 è la DURATA della partita,
 * il 30 è il passo, e la fascia è la FINESTRA che li contiene (12:30–17:30).
 * Sono tre numeri diversi, e questo campo è solo il secondo.
 */
const PASSI = [15, 30, 60, 90, 120];

/**
 * Cambiare una fascia già aperta.
 *
 * ⭐ Si manda SOLO quello che è cambiato davvero, e non l'intera fascia: uno
 * schema di modifica dice «non toccarlo» per ciò che non arriva (§ il commento
 * di `modificaCampoSchema`), quindi rispedire tutto vorrebbe dire riscrivere
 * anche i campi che nessuno ha guardato.
 *
 * ⚠️ Il giorno e il campo sono fra le cose che si possono cambiare: spostare
 * una fascia dal lunedì al martedì, o dedicarla al Campo 3, è una correzione,
 * non un altro mestiere. A impedire una sovrapposizione resta il database.
 */
function Modifica({
  fascia,
  campi,
  chiudi,
}: {
  fascia: Fascia;
  campi: CampoCircolo[];
  chiudi: () => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [giorno, setGiorno] = useState(String(fascia.weekday));
  const [campo, setCampo] = useState(fascia.courtId ?? TUTTI);
  const [inizio, setInizio] = useState(fascia.startsAtTime);
  const [fine, setFine] = useState(fascia.endsAtTime);
  const [passo, setPasso] = useState(String(fascia.slotMinutes));

  const courtIdScelto = campo === TUTTI ? null : campo;
  const cambiato: Record<string, unknown> = {};
  if (Number(giorno) !== fascia.weekday) cambiato.weekday = Number(giorno);
  if (courtIdScelto !== fascia.courtId) cambiato.courtId = courtIdScelto;
  if (inizio !== fascia.startsAtTime) cambiato.startsAtTime = inizio;
  if (fine !== fascia.endsAtTime) cambiato.endsAtTime = fine;
  if (Number(passo) !== fascia.slotMinutes) cambiato.slotMinutes = Number(passo);
  const niente = Object.keys(cambiato).length === 0;

  const salva = useMutation({
    meta: { fatto: 'fascia salvata' },
    mutationFn: () => chiama('PATCH', `/settings/bookable-slots/${fascia.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (!niente) salva.mutate();
      }}
    >
      <div className="riga-campi">
        <label className="campo">
          <span>Giorno</span>
          <select value={giorno} onChange={(e) => setGiorno(e.target.value)}>
            {ORDINE_SETTIMANA.map((i) => (
              <option key={i} value={i}>
                {GIORNI[i]}
              </option>
            ))}
          </select>
        </label>
        <SceltaCampo campi={campi} valore={campo} cambia={setCampo} />
        <CampoOra etichetta="Dalle" required value={inizio} onChange={setInizio} />
        <CampoOra etichetta="Alle" required value={fine} onChange={setFine} />
        <SceltaPasso valore={passo} cambia={setPasso} />
      </div>
      <Spiegazione inizio={inizio} fine={fine} passo={Number(passo)} />
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending || niente}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Annulla
        </button>
      </div>
    </form>
  );
}

/** Il menu dei campi: «tutti i campi» più uno per ciascuno. */
function SceltaCampo({
  campi,
  valore,
  cambia,
}: {
  campi: CampoCircolo[];
  valore: string;
  cambia: (v: string) => void;
}): React.JSX.Element {
  /**
   * ⭐ Si mostrano TUTTI i campi, spenti compresi, con lo stato scritto accanto.
   *
   * 🗣️ Qui c'era `campi.filter((c) => c.active || c.id === valore)`, e il
   * risultato l'ha visto lui: *«c'è solo un campo, invece ce ne dovrebbero
   * essere quattro»*. Un campo spento spariva dal menu **senza dire che
   * esisteva**, quindi la domanda «dove sono gli altri tre?» non aveva risposta
   * da nessuna parte della schermata.
   * 📌 È lo stesso difetto della fascia spenta che spariva dalla striscia: uno
   *    stato non si comunica TOGLIENDO la cosa: si comunica dicendolo.
   */
  return (
    <label className="campo">
      <span>Campo</span>
      <select value={valore} onChange={(e) => cambia(e.target.value)}>
        <option value={TUTTI}>Tutti i campi</option>
        {campi.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {c.active ? '' : ' (spento)'}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Ogni quanto si comincia.
 *
 * ⚠️ È un menu e non una casella libera, ed è deliberato: scritto a mano, questo
 * numero veniva riempito con la lunghezza della fascia — 300 per una finestra
 * 12:30–17:30 — e il database lo rifiutava. Il rifiuto era giusto e il numero
 * sbagliato: non era un passo, era la finestra.
 * 📌 Un valore fuori elenco (arrivato da prima) resta nel menu invece di essere
 *    cambiato di nascosto.
 */
function SceltaPasso({ valore, cambia }: { valore: string; cambia: (v: string) => void }): React.JSX.Element {
  const scelte = PASSI.includes(Number(valore)) ? PASSI : [...PASSI, Number(valore)].sort((a, b) => a - b);
  return (
    <label className="campo">
      <span>Si comincia ogni</span>
      <select value={valore} onChange={(e) => cambia(e.target.value)}>
        {scelte.map((n) => (
          <option key={n} value={n}>
            {n} min
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Cosa vuol dire, in orari veri.
 *
 * ⭐ È la metà che mancava: tre numeri astratti (finestra, passo, durata) non
 * dicono a nessuno a che ora si gioca. Qui si elencano gli inizi che ne escono,
 * che è la cosa che la segreteria riconosce a colpo d'occhio.
 */
function Spiegazione({
  inizio,
  fine,
  passo,
}: {
  inizio: string;
  fine: string;
  passo: number;
}): React.JSX.Element {
  const da = minuti(inizio);
  const a = minuti(fine);
  if (!Number.isFinite(da) || !Number.isFinite(a) || a <= da || passo <= 0) {
    return <p className="tenue piccolo">Metti un orario di inizio e uno di fine.</p>;
  }
  const inizi: string[] = [];
  for (let t = da; t + passo <= a; t += passo) {
    inizi.push(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
  }
  const mostrati = inizi.length > 8 ? [...inizi.slice(0, 6), '…', inizi[inizi.length - 1]!] : inizi;
  return (
    <p className="tenue piccolo">
      Finestra di {a - da} min. Si può cominciare {inizi.length} {inizi.length === 1 ? 'volta' : 'volte'}:{' '}
      {mostrati.join(' · ')}.
      <br />
      La <strong>durata</strong> della partita non la decide questa schermata: si sceglie prenotando, e basta
      che finisca entro le {fine}.
    </p>
  );
}

/**
 * Copiare le fasce SCELTE su altri giorni.
 *
 * 🗣️ Nasce da una sua osservazione: *«non posso duplicare contemporaneamente
 * più fasce»*. Quattro fasce del lunedì sul martedì e sul mercoledì erano otto
 * giri di un pannello che ne faceva uno per volta.
 *
 * ⚠️ È una creazione per ogni coppia (fascia × giorno), non una chiamata sola:
 * il vincolo del database è PER GIORNO E PER CAMPO, quindi una coppia può
 * essere rifiutata mentre le altre passano. ⇒ L'esito si dice **giorno per
 * giorno**, con quante ne sono passate e quali no.
 * 📌 Un «non ha funzionato» su dodici copie di cui undici scritte manderebbe la
 *    segreteria a rifare ciò che c'è già — cioè a sbattere contro dodici
 *    sovrapposizioni.
 */
function DuplicaTante({
  fasce,
  campi,
  svuota,
}: {
  fasce: Fascia[];
  campi: CampoCircolo[];
  svuota: () => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [giorni, setGiorni] = useState<number[]>([]);

  const copia = useMutation({
    meta: { fatto: (d) => perGiorni(d as Esito[], 'fascia copiata') },
    mutationFn: async (): Promise<Esito[]> => {
      const esiti: Esito[] = [];
      // Una per volta e non in parallelo: in fila, l'errore che torna resta
      // attaccato alla sua coppia senza doverlo ricostruire.
      for (const g of giorni) {
        for (const f of fasce) {
          // ⚠️ Una fascia non si copia sul PROPRIO giorno: sarebbe una
          // sovrapposizione perfetta, cioè l'unico rifiuto sicuro. Si salta in
          // silenzio invece di contarlo come un errore, perché non lo è.
          if (f.weekday === g) continue;
          try {
            await chiama('POST', '/settings/bookable-slots', {
              courtId: f.courtId,
              weekday: g,
              startsAtTime: f.startsAtTime,
              endsAtTime: f.endsAtTime,
              slotMinutes: f.slotMinutes,
              validFrom: f.validFrom,
              validTo: f.validTo,
            });
            esiti.push({ giorno: g, fascia: f, errore: null });
          } catch (e) {
            esiti.push({ giorno: g, fascia: f, errore: (e as Error).message });
          }
        }
      }
      return esiti;
    },
    onSuccess: (esiti) => {
      rileggi();
      // Restano scelti solo i giorni che hanno avuto almeno un rifiuto: un
      // secondo giro su un giorno riuscito fallirebbe sulle copie appena
      // scritte, e sembrerebbe un guasto.
      setGiorni((p) => p.filter((g) => esiti.some((e) => e.giorno === g && e.errore !== null)));
    },
  });

  const esiti = copia.data ?? [];
  // Ordinati come si LEGGONO (lunedì…domenica), non come si numerano: l'esito
  // deve scorrere nello stesso ordine delle caselle appena spuntate.
  const perGiorno = [...new Set(esiti.map((e) => e.giorno))].sort(
    (a, b) => ORDINE_SETTIMANA.indexOf(a as 0) - ORDINE_SETTIMANA.indexOf(b as 0),
  );

  return (
    <form
      className="barra-scelta"
      onSubmit={(e) => {
        e.preventDefault();
        if (giorni.length > 0) copia.mutate();
      }}
    >
      <div className="barra-scelta-testa">
        <strong>
          {fasce.length} {fasce.length === 1 ? 'fascia scelta' : 'fasce scelte'}
        </strong>
        <span className="tenue piccolo">
          {fasce.map((f) => `${f.startsAtTime}–${f.endsAtTime}`).join(' · ')}
          {' — '}
          {[...new Set(fasce.map((f) => nomeCampo(f.courtId, campi)))].join(', ')}
        </span>
      </div>

      <div className="giorni-scelta">
        {ORDINE_SETTIMANA.map((i) => (
          <label key={i} className="campo orizzontale">
            <input
              type="checkbox"
              disabled={copia.isPending}
              checked={giorni.includes(i)}
              onChange={(e) => setGiorni((p) => (e.target.checked ? [...p, i] : p.filter((g) => g !== i)))}
            />
            <span>{GIORNI[i]}</span>
          </label>
        ))}
      </div>

      {perGiorno.map((g) => {
        const suoi = esiti.filter((e) => e.giorno === g);
        const ok = suoi.filter((e) => e.errore === null);
        const no = suoi.filter((e) => e.errore !== null);
        return (
          <p
            key={g}
            className={no.length > 0 ? 'guasto' : 'tenue piccolo'}
            role={no.length > 0 ? 'alert' : undefined}
          >
            <strong>{GIORNI[g]}</strong>: {ok.length} {ok.length === 1 ? 'copiata' : 'copiate'}
            {no.length > 0 && (
              <>
                , {no.length} no —{' '}
                {no.map((e) => `${e.fascia.startsAtTime}–${e.fascia.endsAtTime}: ${e.errore}`).join(' · ')}
              </>
            )}
          </p>
        );
      })}
      {/* Il guasto che non appartiene a nessun giorno: la rete caduta a metà giro. */}
      {copia.error ? <Guasto errore={copia.error} /> : null}

      <div className="azioni">
        <button type="submit" disabled={copia.isPending || giorni.length === 0}>
          {copia.isPending
            ? 'Un attimo…'
            : `Duplica su ${giorni.length} ${giorni.length === 1 ? 'giorno' : 'giorni'}`}
        </button>
        <button type="button" className="piatto" onClick={svuota}>
          Togli la selezione
        </button>
      </div>
    </form>
  );
}

/** L'esito di UNA copia: quale fascia, su quale giorno, e com'è andata. */
interface Esito {
  giorno: number;
  fascia: Fascia;
  errore: string | null;
}

function Nuova({ campi }: { campi: CampoCircolo[] }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [giorno, setGiorno] = useState('1');
  const [campo, setCampo] = useState(TUTTI);
  const [inizio, setInizio] = useState('12:30');
  const [fine, setFine] = useState('17:30');
  /**
   * 🗣️ 30 minuti è il valore che ha chiesto lui, e non è un caso: una finestra
   * larga con partite da 90 si riempie bene solo se si può cominciare spesso.
   * ⚠️ Qui c'era `slotMinutes: max(5, fine - inizio)` — cioè il passo uguale a
   *    TUTTA la fascia. Con quel calcolo una finestra 12:30–17:30 chiedeva un
   *    passo di 300 minuti, che il database rifiuta (il massimo è 240): il
   *    rifiuto era giusto, il numero no.
   */
  const [passo, setPasso] = useState('30');

  const crea = useMutation({
    meta: { fatto: 'fascia aggiunta' },
    mutationFn: () =>
      chiama('POST', '/settings/bookable-slots', {
        courtId: campo === TUTTI ? null : campo,
        weekday: Number(giorno),
        startsAtTime: inizio,
        endsAtTime: fine,
        slotMinutes: Number(passo),
      }),
    onSuccess: rileggi,
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Aggiungi una fascia</h3>
      <p className="tenue piccolo">
        Una fascia è la <strong>finestra</strong> in cui si gioca, non la durata di una partita. Due fasce
        dello stesso giorno e dello stesso campo non si possono sovrapporre: a impedirlo è il database, non
        questa schermata.
      </p>
      <div className="riga-campi">
        <label className="campo">
          <span>Giorno</span>
          <select value={giorno} onChange={(e) => setGiorno(e.target.value)}>
            {ORDINE_SETTIMANA.map((i) => (
              <option key={i} value={i}>
                {GIORNI[i]}
              </option>
            ))}
          </select>
        </label>
        <SceltaCampo campi={campi} valore={campo} cambia={setCampo} />
        <CampoOra etichetta="Dalle" required value={inizio} onChange={setInizio} />
        <CampoOra etichetta="Alle" required value={fine} onChange={setFine} />
        <SceltaPasso valore={passo} cambia={setPasso} />
      </div>
      <Spiegazione inizio={inizio} fine={fine} passo={Number(passo)} />
      {/*
        🚨⭐⭐ **LA TRAPPOLA DI QUESTO MODULO, e l'ha trovata una misura del
        18/09/2026 mentre si costruivano le linguette per campo.**

        Una fascia dedicata a un campo **non si aggiunge** a quelle di tutti:
        le **sostituisce**, e per il giorno intero (⇒ `fasce-per-campo.ts`).
        📏 Misurato: un mercoledì da 08:00–23:00 passo 90 dà **10 ore**; creando
        qui una fascia per il Campo 4 dalle 09:00 alle 12:00, il Campo 4 passa a
        **2 ore** e le altre otto spariscono per i soci — senza nessun errore,
        senza che niente lo dica.

        ⚖️ **Perché un avviso e non un divieto**: è un gesto legittimo, e a volte
        è proprio quello che si vuole. Ciò che non va è che sia **invisibile**.
        ⇒ Dal foglio la stessa cosa si fa senza rischio, perché lì si riscrive
        il giorno intero e la copia c'è: è la strada da indicare.
        📌 *Una sostituzione che sembra un'aggiunta è la forma peggiore di un
           difetto: fa esattamente quello che ha promesso, a qualcos'altro.*
      */}
      {campo !== TUTTI && (
        <p className="avvertenza">
          ⚠️ Questa fascia <strong>sostituisce</strong> tutto il {GIORNI[Number(giorno)]?.toLowerCase()} di{' '}
          <strong>{campi.find((c) => c.id === campo)?.name ?? 'quel campo'}</strong>: le altre fasce di quel
          giorno smettono di valere per lui, e i soci non le trovano più. Per cambiare solo un orario tenendo
          il resto, conviene la linguetta del campo sul foglio qui sopra.
        </p>
      )}
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending ? 'Un attimo…' : 'Aggiungi'}
      </button>
    </form>
  );
}
